import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Trash2, Users, Filter, Clock, ChevronRight, Loader2, Target } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { audienceStorage, SavedAudience } from '../services/audienceApi';
import { toast } from 'sonner';
export function AudienceBuilder() {
  const {
    currentWorkspace
  } = useWorkspace();
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<SavedAudience[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  useEffect(() => {
    // Show localStorage data immediately for fast render
    setAudiences(audienceStorage.list(currentWorkspace.name));
    // Then sync from Creator (source of truth) to catch any
    // audiences that might be missing from localStorage
    const syncFromBackend = async () => {
      setIsSyncing(true);
      try {
        const synced = await audienceStorage.syncFromCreator(currentWorkspace.name);
        setAudiences(synced);
      } catch (err) {
        console.warn('Failed to sync audiences from Creator:', err);
        // localStorage data is already shown, so this is non-fatal
      } finally {
        setIsSyncing(false);
      }
    };
    syncFromBackend();
  }, [currentWorkspace.name]);
  const handleDelete = async (id: string) => {
    setDeleteConfirm(null);
    try {
      const result = await audienceStorage.delete(id);
      setAudiences(prev => prev.filter(a => a.id !== id));
      if (result.synced) {
        toast.success('Audience deleted');
      } else {
        toast.warning('Audience deleted locally', {
          description: `Creator sync failed: ${result.error}`
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to delete audience: ${msg}`);
    }
  };
  const filtered = audiences.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q);
  });
  const getRuleSummary = (audience: SavedAudience): string => {
    const count = audience.rules.length;
    if (count === 0) return 'No rules defined';
    const payloadRules = audience.rules.filter(r => r.fieldSource === 'payload').length;
    const standardRules = count - payloadRules;
    const parts: string[] = [];
    if (standardRules > 0) parts.push(`${standardRules} CRM filter${standardRules !== 1 ? 's' : ''}`);
    if (payloadRules > 0) parts.push(`${payloadRules} form data filter${payloadRules !== 1 ? 's' : ''}`);
    return parts.join(' + ') + ` (${audience.logic})`;
  };
  return <div className="space-y-6" data-id="element-1136">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1137">
        <div className="min-w-0" data-id="element-1138">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1139">
            Audience Builder
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1140">
            Create dynamic audiences by filtering contacts using CRM data and
            form submission fields.
            {isSyncing && <span className="inline-flex items-center gap-1 ml-2 text-blue-500" data-id="element-1141">
                <Loader2 className="h-3 w-3 animate-spin" data-id="element-1142" />
                Syncing...
              </span>}
          </p>
        </div>
        <button onClick={() => navigate('/audiences/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-1143">
          <Plus className="h-4 w-4" data-id="element-1144" />
          Create Audience
        </button>
      </div>

      {/* Search */}
      {audiences.length > 0 && <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-1145">
          <div className="relative flex-1 min-w-0" data-id="element-1146">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-1147" />
            <input type="text" placeholder="Search audiences..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-1148" />
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0" data-id="element-1149">
            {filtered.length} audience{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>}

      {/* Audience List */}
      {filtered.length === 0 ? <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-1150">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-1151">
            <Target className="h-8 w-8 text-gray-400" data-id="element-1152" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-1153">
            {searchQuery ? 'No matching audiences' : 'No audiences yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-1154">
            {searchQuery ? 'Try adjusting your search.' : 'Create your first audience to start filtering contacts based on CRM data and form submissions.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/audiences/new')} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1155">
              <Plus className="h-4 w-4" data-id="element-1156" />
              Create First Audience
            </button>}
        </div> : <div className="space-y-3" data-id="element-1157">
          {filtered.map(audience => <div key={audience.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1158">
              <div className="flex items-center gap-4 p-5" data-id="element-1159">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-1160">
                  <Target className="h-5 w-5 text-blue-600" data-id="element-1161" />
                </div>

                <div className="flex-1 min-w-0" data-id="element-1162">
                  <div className="flex items-center gap-2" data-id="element-1163">
                    <Link to={`/audiences/${audience.id}`} className="text-sm font-semibold text-gray-900 hover:text-blue-600 truncate" data-id="element-1164">
                      {audience.name}
                    </Link>
                    {audience.lastCount != null && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-1165">
                        <Users className="h-3 w-3" data-id="element-1166" />
                        {audience.lastCount}
                      </span>}
                  </div>
                  {audience.description && <p className="text-sm text-gray-500 truncate mt-0.5" data-id="element-1167">
                      {audience.description}
                    </p>}
                  <div className="flex items-center gap-3 mt-1.5" data-id="element-1168">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-1169">
                      <Filter className="h-3 w-3" data-id="element-1170" />
                      {getRuleSummary(audience)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-1171">
                      <Clock className="h-3 w-3" data-id="element-1172" />
                      {new Date(audience.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1173">
                  <button onClick={e => {
              e.stopPropagation();
              setDeleteConfirm(audience.id);
            }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Delete audience" data-id="element-1174">
                    <Trash2 className="h-4 w-4" data-id="element-1175" />
                  </button>
                  <Link to={`/audiences/${audience.id}`} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-1176">
                    <ChevronRight className="h-4 w-4" data-id="element-1177" />
                  </Link>
                </div>
              </div>
            </div>)}
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1178">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1179">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-1180" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-1181">
              <div className="flex items-center gap-4" data-id="element-1182">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-1183">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-1184" />
                </div>
                <div data-id="element-1185">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1186">
                    Delete Audience
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-1187">
                    Are you sure? This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-1188">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1189">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700" data-id="element-1190">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}